// ============================================================
// Wer darf was — die zwei Fragen, die die Oberfläche selbst beantwortet.
//
// Das Backend entscheidet (SPEC §2/§4, `rechte.nur_admin`); hier steht nur,
// welche Knöpfe erst gar nicht gezeigt werden. Beides muss dieselbe Antwort
// geben, sonst sieht jemand einen Knopf, der ihm in ein 403 führt.
//
// Warum eine eigene Datei für zwei Zeilen: seit 007 gibt es mit
// `geschaeftsfuehrung` eine ZWEITE Rolle mit Verwaltungsrechten, und die Prüfung
// `rolle === "admin"` stand vorher an acht Stellen verstreut. Acht Stellen sind
// sieben Gelegenheiten, die neue Rolle zu vergessen — und ein vergessener Ort
// fällt nicht auf, weil er nichts kaputtmacht: er versteckt nur einen Knopf.
// ============================================================
import type { Rolle } from "../api/types";

/**
 * Verwaltungsrechte? `geschaeftsfuehrung` zählt wie `admin` (SPEC §12.2) — sie
 * ist eine eigene ANZEIGE, keine eigene Rechtestufe.
 */
export function istVerwaltung(rolle: Rolle | null | undefined): boolean {
  return rolle === "admin" || rolle === "geschaeftsfuehrung";
}

/**
 * Darf jemand ein Projekt anlegen? Seit §36 JEDE angemeldete Rolle — die
 * Spiegelung von `rechte.darf_anlegen(n) = bool(n)` im Backend.
 *
 * Bis §36 waren es nur Verwaltung und Projektleitung („ein Projekt ist ein
 * Stammdatum, kein Beitrag"). Das Stammdatum schützt jetzt nicht mehr die
 * Tür, sondern die Freigabe: was die Mitarbeit anlegt, steht `offen` und ist
 * erst nach der Freigabe im Haus (`darfFreigeben`). Der Name bleibt, weil
 * Karte, Galerie und Board über ihn fragen — EIN Ort, an dem die Regel kippt.
 *
 * `!!rolle` und nicht `true`: ohne Rolle (abgemeldet, Schaufenster) legt
 * niemand an.
 */
export function darfAnlegen(rolle: Rolle | null | undefined): boolean {
  return !!rolle;
}

/**
 * Hat jemand den RANG, der Projekte freigibt (SPEC §36)? Verwaltung und
 * Projektleitung — die Spiegelung von `rechte.darf_freigeben` im Backend.
 *
 * Wer den Rang hat, legt gleich freigegeben an und kommt als Leitung ins Team;
 * alle anderen legen `offen` an und MÜSSEN dabei eine Projektleitung wählen —
 * und zwar eine mit diesem Rang (`freigebendePersonen` in
 * screens/projekt/hilfen.ts). Darum hängen die Vorbelegung „Leitung: ich", das
 * Pflichtfeld und die Auswahl im Anlegeformular an DIESER Frage und nicht an
 * `darfAnlegen` — die beantwortet seit §36 jede Rolle mit Ja.
 *
 * Ob jemand ein BESTIMMTES Projekt freigeben darf, sagt diese Funktion nicht
 * (geändert am 29.09.2026 nach Rückfrage): das darf nur seine Projektleitung,
 * die Geschäftsführung und die Admins. Die Knöpfe „Freigeben" / „Ablehnen"
 * hängen an `Projekt.darf_freigeben` (der Server kennt Leitung und Zustand),
 * die übrigen Fragen an `entscheidetMit` (screens/projekt/hilfen.ts).
 */
export function darfFreigeben(rolle: Rolle | null | undefined): boolean {
  return istVerwaltung(rolle) || rolle === "projektleitung";
}

/**
 * Darf jemand Beträge sehen? **Nur die Geschäftsführung** (SPEC §18.3) — und
 * damit ausdrücklich enger als `istVerwaltung`: `admin` ist die technische
 * Rolle, die die erste Anmeldung im Haus bekommt, und Honorare gehen damit
 * niemanden etwas an.
 *
 * Das ist die Spiegelung von `auth.GELD_ROLLEN`. Die Antwort trägt den
 * Schlüssel `honorar_eur` gar nicht, wenn das Recht fehlt; diese Funktion sagt
 * der Oberfläche, ob sie „—" (nicht erfasst) oder „gesperrt" zeigen muss.
 */
export function darfGeldSehen(rolle: Rolle | null | undefined): boolean {
  return rolle === "geschaeftsfuehrung";
}
